import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCheck, AlertCircle, MessageSquare, Paperclip, Mail, Bell } from "lucide-react";
import AppShell, { ACCENT, ACCENT_DEEP, ACCENT_SOFT, ACCENT_LINE } from "../components/AppShell";
import { BRAND } from "@/brand";
import { useAuth } from "../hooks/useAuth";
import { API_BASE_URL } from "@/utils/env";
import { getAuthTokens } from "@/utils/auth";
import { setUnreadCount } from "@/utils/unread";

export type Notification = {
    id: string;
    priority: "CRITICAL" | "HIGH" | "NORMAL";
    type: "alert" | "message" | "file" | "email";
    title: string;
    sender: string;
    senderRole: string;
    timestamp: string;
    sentAt?: string;
    preview: string | null;
    attachment?: { name: string; size: string; fileType: string };
    read: boolean;
};

// CRITICAL and HIGH keep their own semantics — red still means red. NORMAL is
// the everyday case and carries no warning, so it takes the brand accent rather
// than a blue that reads as a second alert level against the cream page.
const PRIORITY_CONFIG = {
    CRITICAL: { label: "CRITICAL", bg: "#FEF2F2", color: "#DC2626", borderColor: "#EF4444" },
    HIGH: { label: "HIGH", bg: "#F0FDFA", color: "#0F766E", borderColor: "#0D9488" },
    NORMAL: { label: "NORMAL", bg: ACCENT_SOFT, color: ACCENT_DEEP, borderColor: ACCENT_LINE },
};

const TYPE_ICON = {
    alert: <AlertCircle className="w-5 h-5" />,
    message: <MessageSquare className="w-5 h-5" />,
    file: <Paperclip className="w-5 h-5" />,
    email: <Mail className="w-5 h-5" />,
};

const TYPE_ICON_BG = {
    alert: { bg: "#FEF2F2", color: "#DC2626" },
    message: { bg: ACCENT_SOFT, color: ACCENT_DEEP },
    file: { bg: "#F0FDFA", color: "#0F766E" },
    email: { bg: "#F3F4F6", color: "#6B7280" },
};

// Priority Digest palette.
//
// This was a full-bleed gold gradient, and a whole surface of #FFD54F is the
// loudest thing in the app — loud enough that the two elements that carry the
// information, the count and the button, had nothing left to stand out against.
// Inverted: the card takes the brand's navy and gold goes on the count and the
// CTA only. Same two colours, opposite proportions, and now the eye lands on
// the number.
const IS_GOLD = BRAND === "corporate";
const DIGEST_BG = IS_GOLD
    ? "linear-gradient(140deg,#1B2C45 0%,#17263A 52%,#0C1B30 100%)"
    : "linear-gradient(135deg, #1D74F5 0%, #1e40af 100%)";
const DIGEST_INK = IS_GOLD ? "#FFFFFF" : "#FFFFFF";
const DIGEST_MUTED = IS_GOLD ? "rgba(214,226,242,.76)" : "#DBEAFE";
/** The count, and anything else worth pulling out of the copy. */
const DIGEST_HI = IS_GOLD ? "#FFD54F" : "#FFFFFF";
/** Button fill, and the ink that survives on it. */
const DIGEST_BTN_BG = IS_GOLD ? "#FFD54F" : "#FFFFFF";
const DIGEST_BTN_INK = IS_GOLD ? "#17263A" : "#1D4ED8";

type FilterType = "all" | "unread" | "alerts" | "messages" | "files";

const READ_STORAGE_KEY = "connct_read_notifications";

function getReadSet(): Set<string> {
    try {
        const stored = localStorage.getItem(READ_STORAGE_KEY);
        return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
        return new Set();
    }
}

function saveReadSet(set: Set<string>) {
    try {
        localStorage.setItem(READ_STORAGE_KEY, JSON.stringify([...set]));
    } catch {}
}

function formatTimestamp(sentAt?: string): string {
    if (!sentAt) return "";
    try {
        const date = new Date(sentAt);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffHours = diffMs / (1000 * 60 * 60);
        if (diffHours < 1) return `${Math.round(diffMs / 60000)} min ago`;
        if (diffHours < 24) return `${Math.round(diffHours)} hours ago`;
        if (diffHours < 48) return "Yesterday";
        return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return sentAt;
    }
}

export default function Notifications() {
    const navigate = useNavigate();
    const { user: authUser, logout } = useAuth();
    const [filter, setFilter] = useState<FilterType>("all");
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(true);
    const [readSet, setReadSet] = useState<Set<string>>(getReadSet);

    useEffect(() => {
        if (!authUser?.uid) return;
        const fetchNotifications = async () => {
            try {
                const tokens = getAuthTokens();
                const idToken = tokens?.idToken;
                const resp = await fetch(`${API_BASE_URL}/auth/user/${authUser.uid}`, {
                    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
                });
                if (resp.ok) {
                    const json = await resp.json();
                    const userData = json.success ? json.user : json;
                    const rawNotifs = userData?.notifications;
                    const list: Notification[] = (rawNotifs && typeof rawNotifs === "object")
                        ? Object.entries(rawNotifs).map(([key, val]: [string, any]) => ({
                            id: key,
                            priority: (val.priority as Notification["priority"]) || "NORMAL",
                            type: (val.type as Notification["type"]) || "message",
                            title: val.title || "Notification",
                            sender: val.sender || "Admin",
                            senderRole: val.senderRole || "Admin",
                            timestamp: formatTimestamp(val.sentAt || val.timestamp),
                            sentAt: val.sentAt || val.timestamp,
                            preview: val.preview || val.message || null,
                            read: val.read || false,
                        }))
                        : [];
                    // Sort newest first
                    list.sort((a, b) => {
                        const aTime = a.sentAt ? new Date(a.sentAt).getTime() : 0;
                        const bTime = b.sentAt ? new Date(b.sentAt).getTime() : 0;
                        return bTime - aTime;
                    });
                    // Merge admin-set requests (documents/actions the client must provide) as actionable
                    // "alert" notifications, so they appear in the Notification Center AND at the top of
                    // the dashboard — Bilal's "same two places" for both onboarding & active clients.
                    const ops: any = userData?.adminOps || {};
                    const KL: any = { document: "Document", "id-renewal": "ID renewal", update: "Updated document", info: "Additional info" };
                    const reqNotifs: Notification[] = [
                        ...(Array.isArray(ops.acc?.requests) ? ops.acc.requests : []),
                        ...(Array.isArray(ops.con?.requests) ? ops.con.requests : []),
                    ]
                        .filter((r: any) => r && r.status !== "received")
                        .map((r: any) => ({
                            id: "req-" + (r.id || r.label || "item"),
                            priority: "HIGH" as const,
                            type: "alert" as const,
                            title: "Action needed: " + (KL[r.kind] || "Request"),
                            sender: "Your Conncct team",
                            senderRole: "Admin",
                            timestamp: "",
                            sentAt: undefined,
                            preview: (r.label || "Requested item") + (r.note ? ": " + r.note : ""),
                            read: false,
                        }));
                    setNotifications([...reqNotifs, ...list]);
                    setUnreadCount(reqNotifs.length + list.filter(n => !readSet.has(n.id)).length);
                }
            } catch (e) {
                console.error("Error fetching notifications:", e);
            } finally {
                setLoading(false);
            }
        };
        fetchNotifications();
    }, [authUser]);

    const markAllRead = () => {
        const newSet = new Set(notifications.map(n => n.id));
        setReadSet(newSet);
        saveReadSet(newSet);
        setUnreadCount(0);
    };

    const isRead = (id: string) => readSet.has(id);

    const filtered = notifications.filter(n => {
        if (filter === "unread") return !isRead(n.id);
        if (filter === "alerts") return n.type === "alert";
        if (filter === "messages") return n.type === "message";
        if (filter === "files") return n.type === "file";
        return true;
    });

    const counts = {
        all: notifications.length,
        unread: notifications.filter(n => !isRead(n.id)).length,
        alerts: notifications.filter(n => n.type === "alert").length,
        messages: notifications.filter(n => n.type === "message").length,
        files: notifications.filter(n => n.type === "file").length,
    };

    const handleNotificationClick = (id: string) => {
        const newSet = new Set([...readSet, id]);
        setReadSet(newSet);
        saveReadSet(newSet);
        setUnreadCount(notifications.filter(n => !newSet.has(n.id)).length);
        // Admin-set requests have no detail doc — send the client to the Data Room to act on them.
        if (id.startsWith("req-")) { navigate("/data-room"); return; }
        navigate(`/notifications/${id}`);
    };

    const FILTERS: { id: FilterType; label: string; icon: React.ReactNode }[] = [
        { id: "all", label: "All", icon: <Bell className="w-4 h-4" /> },
        { id: "unread", label: "Unread", icon: <Mail className="w-4 h-4" /> },
        { id: "alerts", label: "Alerts", icon: <AlertCircle className="w-4 h-4" /> },
        { id: "messages", label: "Messages", icon: <MessageSquare className="w-4 h-4" /> },
        { id: "files", label: "Files", icon: <Paperclip className="w-4 h-4" /> },
    ];

    return (
        <AppShell
            title="Notifications"
            subtitle="Manage your alerts, messages, and administrative updates."
            crumb="Notifications"
            onLogout={logout}
            actions={
                <button
                    onClick={markAllRead}
                    className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition shadow-sm shrink-0"
                >
                    <CheckCheck className="w-4 h-4" />
                    Mark all as read
                </button>
            }
        >
                {/* Body: filter panel + list */}
                <div className="flex flex-col lg:flex-row gap-5 items-start">

                    {/* Filter panel */}
                    <div className="w-full lg:w-56 shrink-0 space-y-4">
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 px-1">Filter by Type</p>
                            <div className="space-y-1">
                                {FILTERS.map(f => (
                                    <button
                                        key={f.id}
                                        onClick={() => setFilter(f.id)}
                                        className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-semibold transition-all"
                                        style={{
                                            backgroundColor: filter === f.id ? ACCENT : "transparent",
                                            color: filter === f.id ? "#fff" : "#374151",
                                        }}
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <span style={{ opacity: filter === f.id ? 1 : 0.6 }}>{f.icon}</span>
                                            {f.label}
                                        </div>
                                        <span
                                            className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                                            style={{
                                                backgroundColor: filter === f.id ? "rgba(255,255,255,0.2)" : "#F3F4F6",
                                                color: filter === f.id ? "#fff" : "#6B7280",
                                            }}
                                        >
                                            {counts[f.id]}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Priority Digest — the one dark block on a cream page, which is
                            how the marketing site uses navy too. A hairline lip along the
                            top edge keeps it from reading as a flat cut-out. */}
                        <div
                            className="rounded-2xl p-5"
                            style={{
                                background: DIGEST_BG,
                                color: DIGEST_INK,
                                boxShadow: IS_GOLD
                                    ? "inset 0 1px 0 rgba(255,213,79,.28), 0 14px 30px -18px rgba(23,38,58,.85)"
                                    : "0 12px 28px -18px rgba(29,116,245,.9)",
                            }}
                        >
                            <p className="text-base font-bold mb-2">Priority Digest</p>
                            <p className="text-xs leading-relaxed mb-5" style={{ color: DIGEST_MUTED }}>
                                You have{" "}
                                <span className="font-bold text-base" style={{ color: DIGEST_HI }}>
                                    {notifications.filter(n => n.priority === "CRITICAL" || n.priority === "HIGH").length}
                                </span>{" "}
                                high-priority items requiring immediate attention today.
                            </p>
                            <button
                                onClick={() => setFilter("alerts")}
                                className="w-full rounded-xl py-2.5 text-sm font-bold transition hover:opacity-90"
                                style={{ background: DIGEST_BTN_BG, color: DIGEST_BTN_INK }}
                            >
                                View Urgent
                            </button>
                        </div>
                    </div>

                    {/* Notification list */}
                    <div className="flex-1 space-y-3 min-w-0">
                        {loading ? (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-3" style={{ borderBottomColor: ACCENT }} />
                                <p className="text-sm text-gray-400">Loading notifications...</p>
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
                                <Bell className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                                <p className="text-sm font-semibold text-gray-400">No notifications in this category</p>
                                {notifications.length === 0 && (
                                    <p className="text-xs text-gray-400 mt-1">Notifications from the admin will appear here</p>
                                )}
                            </div>
                        ) : (
                            filtered.map(n => {
                                const pc = PRIORITY_CONFIG[n.priority] || PRIORITY_CONFIG.NORMAL;
                                const iconStyle = TYPE_ICON_BG[n.type] || TYPE_ICON_BG.message;
                                const read = isRead(n.id);
                                return (
                                    <div
                                        key={n.id}
                                        onClick={() => handleNotificationClick(n.id)}
                                        className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex cursor-pointer hover:shadow-md transition-shadow relative"
                                        style={{ borderLeft: `4px solid ${read ? "#E5E7EB" : pc.borderColor}` }}
                                    >
                                        {/* Icon */}
                                        <div className="flex-shrink-0 flex items-start pt-5 pl-4 pr-3">
                                            <div
                                                className="w-10 h-10 rounded-xl flex items-center justify-center"
                                                style={{ backgroundColor: read ? "#F3F4F6" : iconStyle.bg, color: read ? "#9CA3AF" : iconStyle.color }}
                                            >
                                                {TYPE_ICON[n.type] || TYPE_ICON.message}
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0 py-4 pr-5">
                                            <div className="flex items-start justify-between gap-3 mb-1.5">
                                                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                                    <span
                                                        className="text-[10px] font-black px-2 py-0.5 rounded-md tracking-wide shrink-0"
                                                        style={{ backgroundColor: read ? "#F3F4F6" : pc.bg, color: read ? "#9CA3AF" : pc.color }}
                                                    >
                                                        {n.priority}
                                                    </span>
                                                    <h3 className={`text-sm font-bold truncate ${read ? "text-gray-400" : "text-gray-900"}`}>
                                                        {n.title}
                                                    </h3>
                                                </div>
                                                {/* The unread dot rides in this row rather than being
                                                    absolutely positioned — pinned to the card corner it
                                                    landed on top of the date. */}
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <span className="text-[11px] text-gray-400">{n.timestamp}</span>
                                                    {!read && (
                                                        <span
                                                            className="w-2 h-2 rounded-full"
                                                            style={{ backgroundColor: pc.borderColor }}
                                                            aria-label="Unread"
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            <p className="text-xs font-semibold text-gray-500 mb-1.5">
                                                {n.sender} <span className="font-normal">• {n.senderRole}</span>
                                            </p>

                                            {n.preview && (
                                                <p className={`text-xs leading-relaxed line-clamp-2 ${read ? "text-gray-400" : "text-gray-600"}`}>
                                                    {n.preview}
                                                </p>
                                            )}

                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
        </AppShell>
    );
}
